import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import ToastManager from './components/ToastManager';
import ViewRenderer from "./components/ViewRenderer";
import DebugInfo from "./components/debug/DebugInfo";
import MiniGolfAPI from "./services/Api";

const useQueryParams = () => {
  const [params, setParams] = useState({});

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const paramsObj = {};

    for (const [key, value] of searchParams.entries()) {
      if (value === 'true') {
        paramsObj[key] = true;
      } else if (value === 'false') {
        paramsObj[key] = false;
      } else {
        paramsObj[key] = value;
      }
    }

    setParams(paramsObj);
    console.log('🔍 Query params:', paramsObj);
  }, []);

  return params;
};

const App = () => {
  const queryParams = useQueryParams();

  const [gameState, setGameState] = useState({
    players: [],
    holes: [],
    availableAvatars: []
  });
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [api] = useState(new MiniGolfAPI());

  const [toastApi, setToastApi] = useState(null);

  const showToast = useCallback((message, type = 'info', duration = 4000) => {
    if (toastApi) {
      toastApi.addToast(message, type, duration);
    } else {
      // Fallback do console jeśli toast nie jest gotowy
      console.log(`Toast (${type}): ${message}`);
    }
  }, [toastApi]);

  const showSuccess = useCallback((message) => showToast(message, 'success', 2000), [showToast]);
  const showError = useCallback((message) => showToast(message, 'error', 5000), [showToast]);
  const showWarning = useCallback((message) => showToast(message, 'warning', 4000), [showToast]);
  const showInfo = useCallback((message) => showToast(message, 'info', 2000), [showToast]);

  const handleToastCreate = useCallback((api) => {
    setToastApi(api);
  }, []);

  useEffect(() => {
    const initializeConnection = async () => {
      try {
        api.initializeSocket((newGameState) => {
          console.log('🔄 Aktualizacja stanu gry:', newGameState);
          setGameState(prevState => ({
            ...prevState,
            ...newGameState
          }));
          setConnectionStatus('connected');

          if (connectionStatus === 'connecting') {
            showSuccess('Połączono z serwerem! 🔌');
          }
        });
      } catch (error) {
        console.error('❌ Błąd inicjalizacji socket:', error);
        setConnectionStatus('error');
        showError('Błąd połączenia z serwerem! ❌');
      }
    };

    const loadInitialData = async () => {
      try {
        console.log('🔄 Ładowanie danych początkowych...');

        const [avatars, players, holes] = await Promise.all([
          api.getAvailableAvatars(),
          api.getPlayers(),
          api.getHoles()
        ]);

        console.log('✅ Dane załadowane:', {
          avatars: avatars?.length,
          players: players?.length,
          holes: holes?.length
        });

        setGameState(prevState => ({
          ...prevState,
          players: players || [],
          holes: holes || [],
          availableAvatars: avatars || []
        }));

        setConnectionStatus('connected');
        showInfo('Dane załadowane pomyślnie! 📊');
      } catch (error) {
        console.error('❌ Błąd ładowania danych:', error);
        setConnectionStatus('error');
        showError('Błąd ładowania danych! ❌');
      }
    };

    initializeConnection();
    loadInitialData();

    return () => {
      api.disconnect();
    };
  }, [api, showSuccess, showError, showInfo, connectionStatus]);

  if (connectionStatus === 'connecting') {
    return (
        <div className="App loading">
          <div className="loading-screen">
            <h1>🏌️ MiniGolf 3000 🏌️</h1>
            <div className="loading-spinner"></div>
            <p>Łączenie z serwerem...</p>
          </div>
          <ToastManager onToastCreate={handleToastCreate} />
        </div>
    );
  }

  if (connectionStatus === 'error') {
    return (
        <div className="App error">
          <div className="error-screen">
            <h1>🏌️ MiniGolf 3000 🏌️</h1>
            <div className="error-message">
              <h2>❌ Błąd połączenia</h2>
              <p>Nie można połączyć się z serwerem</p>
              <button onClick={() => window.location.reload()}>
                🔄 Spróbuj ponownie
              </button>
            </div>
          </div>
          <ToastManager onToastCreate={handleToastCreate} />
        </div>
    );
  }

  return (
      <div className={`App ${queryParams.fullscreen ? 'fullscreen' : ''} ${queryParams.theme || ''}`}>
        <ViewRenderer
            gameState={gameState}
            api={api}
            queryParams={queryParams}
            connectionStatus={connectionStatus}
            showToast={showToast}
            showSuccess={showSuccess}
            showError={showError}
            showWarning={showWarning}
            showInfo={showInfo}
        />

        <ToastManager onToastCreate={handleToastCreate} />

        <DebugInfo
            queryParams={queryParams}
            gameState={gameState}
            showToast={showToast}
            showSuccess={showSuccess}
            showError={showError}
            showWarning={showWarning}
            showInfo={showInfo}
        />
      </div>
  );
};

export default App;